/**
 * Communication interface between Login Hub pages and their host environments:
 * - Native mobile apps (Android/iOS) via injected LoginHubClientBridge in WebView
 * - WebGL/iFrame contexts via postMessage API
 */
import { isWebPlatform } from '../components/forms/login-hub/common/login-hub-helpers';

declare global {
	// Injected by LoginHub mobile WebView.
	/* eslint-disable @typescript-eslint/no-explicit-any, no-var */ // noinspection ES6ConvertVarToLetConst
	var LoginHubClientBridge: any;
	/* eslint-enable @typescript-eslint/no-explicit-any, no-var */
}

/**
 * Base class defining the communication protocol interface
 */
abstract class LoginHubBridge {
	/**
	 * Returns the BCP47 language tag (e.g.: "de", "de-DE") from the URL locale parameter.
	 * @returns Locale string or null if not available. If not available means, an old client package is used.
	 */
	getLocale(): string | null {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get('locale');
	}

	/**
	 * Closes the webview/iframe and optionally passes data back to the host
	 * @param jsonPayload Optional JSON string with playerId and/or lpsToken
	 */
	abstract close(jsonPayload?: string): void;

	protected logError(message: string): void {
		console.error(`[LoginHubBridge] ${message}`);
	}

	protected logInfo(message: string): void {
		console.info(`[LoginHubBridge] ${message}`);
	}
}

/**
 * Implementation for native platforms (Android/iOS).
 * The LoginHubClientBridge object is injected by the native code.
 */
class NativeBridge extends LoginHubBridge {
	constructor() {
		super();
		if (typeof LoginHubClientBridge === 'undefined') {
			this.logError('LoginHubClientBridge interface was not injected. This should never happen in production.');
		}
	}

	getLocale(): string | null {
		const urlLocale = super.getLocale();
		if (urlLocale) {
			return urlLocale;
		}

		try {
			// Fallback for old clients.
			const locale = LoginHubClientBridge.getLocale();
			this.logInfo(`Locale from native bridge: ${locale}`);
			return locale;
		} catch (error) {
			// This should never happen, it would mean, the client package doesn't send the local query parameter,
			// but it also doesn't have the bridge method.
			this.logError(`Failed to get locale from native bridge: ${error}`);
			return null;
		}
	}

	close(jsonPayload?: string): void {
		try {
			if (jsonPayload) {
				this.logInfo(`Closing with payload: ${jsonPayload}`);
				LoginHubClientBridge.close(jsonPayload);
			} else {
				this.logInfo('Closing without payload');
				LoginHubClientBridge.close();
			}
		} catch (error) {
			this.logError(`Failed to close via native bridge: ${error}`);
		}
	}
}

/**
 * Implementation for WebGL/iFrame using postMessage API
 */
class PostMessageBridge extends LoginHubBridge {
	close(jsonPayload?: string): void {
		const message = {
			type: 'LoginHub',
			action: 'close',
			jsonPayload: jsonPayload || null
		};

		this.logInfo(`Sending postMessage to parent: ${JSON.stringify(message)}`);

		// Send to LoginHub.jslib that is hosted in the Unity WebGL build.
		// Using '*' as targetOrigin for now - in production, specify the actual origin.
		window.parent.postMessage(message, '*');
	}
}

function createBridge(): LoginHubBridge {
	const bridge = isWebPlatform() ? new PostMessageBridge() : new NativeBridge() ;
	console.info(`Using: ${bridge.constructor.name}`);
	return bridge;
}

// Singleton instance
let bridgeInstance: LoginHubBridge | null = null;

/**
 * Gets the singleton bridge instance
 * @returns LoginHubBridge instance
 */
export function getLoginHubBridge(): LoginHubBridge {
	if (!bridgeInstance) {
		bridgeInstance = createBridge();
	}
	return bridgeInstance;
}
